import { useSuspenseQueries } from '@tanstack/react-query'

import { coinMarketQueryOptions } from '@/services/coin/market'

interface CoinWithSlug {
  slug: string
}

interface UseTradableCoinsOptions<T extends CoinWithSlug> {
  coins: T[] | undefined
  maxCount?: number
}

/**
 * Custom hook to fetch market data for coins and prioritize tradable ones
 * @param coins - Array of coins to process (must have a slug property)
 * @param maxCount - Maximum number of coins to return (default: 2)
 * @returns Array of display coins sorted by tradability
 */
export default function useTradableCoins<T extends CoinWithSlug>({
  coins,
  maxCount = 2,
}: UseTradableCoinsOptions<T>): T[] {
  // Batch fetch market data for all candidates
  const marketQueries = useSuspenseQueries({
    queries: (coins || []).map(coin => coinMarketQueryOptions(coin.slug, true)),
  })

  if (!coins || coins.length === 0) return []

  // Collect tradable and non-tradable coins separately
  const tradableCoins: T[] = []
  const nonTradableCoins: T[] = []

  for (let i = 0; i < marketQueries.length; i++) {
    const marketData = marketQueries[i].data
    const coin = coins[i]

    if (marketData && (marketData.app_tradable || marketData.exchange_tradable)) {
      tradableCoins.push(coin)
    } else if (marketData) {
      nonTradableCoins.push(coin)
    }
  }

  // Prioritize tradable coins, then fill with non-tradable ones
  return [...tradableCoins, ...nonTradableCoins].slice(0, maxCount)
}
